// Dashboard principal de la Fan Zone
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Trophy, ArrowLeft, Loader2, RefreshCw, User } from 'lucide-react';
import UserHeader from './shared/UserHeader';
import GameCard from './shared/GameCard';
import { GAMES_CONFIG, calculateLevel } from './services/fanZoneConfig';
import { 
  getOrCreateUserProfile, 
  getUserRank, 
  hasPlayedToday,
  getFanZoneStats 
} from './services/fanZoneService';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

// Scrollbar styles pour la Fan Zone
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .fanzone-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
  .fanzone-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.6) 0%, rgba(189, 255, 0, 0.4) 100%);
  }
`;

const FanZoneDashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [userRank, setUserRank] = useState({ rank: 0, totalPlayers: 0 });
  const [gamesStatus, setGamesStatus] = useState({});
  const [selectedDifficulties, setSelectedDifficulties] = useState({
    quiEstCe: 'facile',
    wordle: 'facile',
    puzzle: 'facile'
  });
  const [stats, setStats] = useState(null);

  // Injecter les styles de scrollbar
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Charger les données utilisateur
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        
        // Nom dynamique de l'utilisateur
        const displayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Fan EFAFC';
        
        // Charger le profil utilisateur (avec fallback)
        let profile = null;
        try {
          profile = await getOrCreateUserProfile(currentUser.uid, displayName);
        } catch (e) {
          logger.warn('Permission Firebase ignorée, profil local utilisé');
          // Profil local par défaut avec calcul dynamique du niveau
          const levelInfo = calculateLevel(0);
          profile = {
            id: currentUser.uid,
            pseudo: displayName,
            totalXP: 0,
            level: 1,
            levelInfo,
            gamesPlayed: { pronostics: 0, quiEstCe: 0, wordle: 0, puzzle: 0 }
          };
        }
        
        // Utiliser le nom Firebase si le pseudo est générique
        if (profile.pseudo === 'Fan EFAFC' && displayName !== 'Fan EFAFC') {
          profile.pseudo = displayName;
        }
        
        setUserProfile(profile);
        
        // Charger le rang (ignorer erreurs)
        try {
          const rank = await getUserRank(currentUser.uid);
          setUserRank(rank);
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour le rang');
          setUserRank({ rank: 1, totalPlayers: 1 });
        }
        
        // Vérifier quels jeux ont été joués aujourd'hui (ignorer erreurs)
        const status = {};
        for (const gameId of Object.keys(GAMES_CONFIG)) {
          if (GAMES_CONFIG[gameId].frequency === 'daily') {
            try {
              status[gameId] = await hasPlayedToday(currentUser.uid, gameId);
            } catch (e) {
              status[gameId] = false;
            }
          } else {
            status[gameId] = false;
          }
        }
        setGamesStatus(status);
        
        // Charger les stats globales (ignorer erreurs)
        try {
          const globalStats = await getFanZoneStats();
          setStats(globalStats);
        } catch (e) {
          setStats({ totalPlayers: 1, totalGamesPlayed: 0, totalXP: 0, averageXP: 0 });
        }
        
      } catch (error) {
        logger.error('Erreur chargement dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, navigate]);

  const handleGameClick = (gameId, difficulty) => {
    const game = GAMES_CONFIG[gameId];
    
    if (gamesStatus[gameId]) {
      toast('Tu as déjà joué à ce jeu aujourd\'hui !', { icon: '🕐' });
      return;
    }

    // Navigation vers le jeu avec la difficulté
    const diffParam = game.hasDifficulty ? `?difficulty=${difficulty}` : '';
    navigate(`/fanzone/${gameId}${diffParam}`);
  };

  const handleDifficultyChange = (gameId, difficulty) => {
    setSelectedDifficulties(prev => ({
      ...prev,
      [gameId]: difficulty
    }));
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 25%, #0a1628 50%, #091320 75%, #050a12 100%)' }}
      >
        <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-20"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 25%, #0a1628 50%, #091320 75%, #050a12 100%)' }}
    >
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#FFE400]/5 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#BDFF00]/5 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 pt-6">
        {/* Header avec navigation */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={() => navigate('/fanzone')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/fanzone/profil')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            >
              <User className="w-5 h-5" />
              <span className="hidden sm:inline">Profil</span>
            </button>
            <button
              onClick={() => navigate('/fanzone/classement')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-[#FFE400] transition-colors"
            >
              <Trophy className="w-5 h-5" />
              <span className="hidden sm:inline">Classement</span>
            </button>
          </div>
        </motion.div>

        {/* User Header */}
        <UserHeader 
          userProfile={userProfile} 
          rank={userRank.rank} 
          totalPlayers={userRank.totalPlayers}
        />

        {/* Section Défis du jour */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Défis du jour</h2>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <RefreshCw className="w-4 h-4" />
              <span>Réinitialisation à minuit</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(GAMES_CONFIG)
              .filter(([_, game]) => game.frequency === 'daily')
              .map(([gameId, game], index) => (
                <motion.div
                  key={gameId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <GameCard
                    game={game}
                    isCompleted={gamesStatus[gameId]}
                    selectedDifficulty={selectedDifficulties[gameId]}
                    onDifficultyChange={(diff) => handleDifficultyChange(gameId, diff)}
                    onClick={(diff) => handleGameClick(gameId, diff)}
                  />
                </motion.div>
              ))
            }
          </div>
        </motion.div>

        {/* Section Pronostics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <h2 className="text-xl font-bold text-white mb-4">Pronostics</h2>
          
          <div className="grid grid-cols-1 gap-4">
            {Object.entries(GAMES_CONFIG)
              .filter(([_, game]) => game.frequency === 'match')
              .map(([gameId, game]) => (
                <GameCard
                  key={gameId}
                  game={game}
                  isCompleted={gamesStatus[gameId]}
                  onClick={() => handleGameClick(gameId)}
                />
              ))
            }
          </div>
        </motion.div>

        {/* Stats globales */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-8 mb-8"
          >
            <h2 className="text-xl font-bold text-white mb-4">Statistiques Fan Zone</h2>
            
            <div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-[#FFE400]">{stats.totalPlayers}</div>
                <div className="text-xs text-gray-400">Joueurs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#BDFF00]">{stats.totalGamesPlayed}</div>
                <div className="text-xs text-gray-400">Parties jouées</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{stats.totalXP?.toLocaleString()}</div>
                <div className="text-xs text-gray-400">XP total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-300">{stats.averageXP}</div>
                <div className="text-xs text-gray-400">XP moyen</div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FanZoneDashboard;
